import { useNavigationCommands } from '@/components/command-palette/useNavigationCommands'
import { usePlanExpiration } from '@/hooks/usePlanExpiration'
import { useSequence } from '@/lib/useSequence'
import { useMatches } from '@tanstack/react-router'
import { useMemo } from 'react'
import { useDeviceActionStarter } from './useDeviceActionStarter'
import { useGoToStarter } from './useGoToStarter'
import { useSettingsStarter } from './useSettingsStarter'

const DEVICE_MANAGEMENT_ROUTE = '/orgs/$orgId/projects/$tenantId/device-management/$status'

/**
 * Every chord sequence (`G then X`, `S then X`, `U then X`) shares one engine instance, mounted once,
 * globally, since it has no control of its own to live inside. Two independent engines would each see
 * every keydown on `window`, so a key that both completes one starter's sequence and begins another's
 * (every letter here is some other starter's own key too) would do both at once instead of just the
 * first.
 */
export function SequenceShortcutsHost() {
	const { isAccessBlocked } = usePlanExpiration()
	const commands = useNavigationCommands(isAccessBlocked)
	const routePath = useMatches({ select: matches => matches.at(-1)?.fullPath })
	const onDeviceManagement = routePath === DEVICE_MANAGEMENT_ROUTE

	const goTo = useGoToStarter(commands)
	const settings = useSettingsStarter(commands)
	// Unlike G/S, U only arms on Device Management — it is still a continuation of G then U and S then
	// U everywhere else, which the pending-sequence check in useSequence resolves ahead of any fresh
	// starter lookup, so this scoping never fights those.
	const deviceAction = useDeviceActionStarter()

	const starters = useMemo(
		() => (onDeviceManagement ? [goTo, settings, deviceAction] : [goTo, settings]),
		[goTo, settings, deviceAction, onDeviceManagement],
	)
	useSequence(starters)

	return null
}
